import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../../../store/types';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import ProductItem from '../product/ProductItem';
import {
  CategoryInterface,
  OpeningHoursInterface,
  ProductInterface,
  SmartPricingRule,
} from '../../../services/exports/Interfaces';
import useTheme from '../../../hooks/ui/useTheme';
import { useIntersection } from '../../../hooks/utility/useIntersection';
import useMenu from '../../../hooks/menu/useMenu';
import useScreenType from '../../../hooks/utility/useScreenType';
import { CategoryType, SmartPricingRuleModifierType } from '../../../services/exports/Constants';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import classnames from 'classnames';
import { Clock } from 'lucide-react';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { WithActualPrice } from '../../../providers/MenuProvider';
import WorkScheduleHelpers from '../../../services/helpers/WorkScheduleHelpers';
import { selectWorkSchedule } from '../../../store/slices/InitialDataSlice';
import { SCHEDULE_CLOSED_TEXT_CLASS, SCHEDULE_OPEN_TEXT_CLASS } from '../restricted/constants';
import useCategoryRestriction from '../../../hooks/menu/useCategoryRestriction';

interface Props {
  category: CategoryInterface<WithActualPrice<ProductInterface>>;
  index: number;
  isActive: boolean;
  isDesktop: boolean;
  loading?: boolean;
  toggleProduct: (product: ProductInterface) => void;
  onChangeVisibleIndex: (index: number) => void;
}

export default function CategoryItem(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:CategoryItem' });

  const { category, index, isActive, toggleProduct, onChangeVisibleIndex } = props;

  const theme = useTheme();
  const ref = useRef();

  const { formatCurrency, formatScheduleUnit } = useAppMethods();

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const workSchedule = useSelector(selectWorkSchedule);
  const { selectedMenu, productHeight } = useMenu();
  const { isMobileApp } = useMobileApp();
  const { isMobile } = useScreenType();
  const getRestriction = useCategoryRestriction();
  const isTwoColumn = !isMobile;

  const [visible, setVisible] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const searchString = searchParams.get('search');
  const filteredProducts = useMemo(() => {
    if (!searchString) {
      return category?.products;
    }

    return category?.products.filter((product) => new RegExp(searchString, 'i').test(product.name));
  }, [searchString, category?.products]);

  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const smartPricingRule: SmartPricingRule | null = useMemo(() => {
    if (!cached_order?.method) {
      return null;
    }

    const rule = [category?.smart_pricing_rules, selectedMenu?.smart_pricing_rules]
      .flatMap((rules) => rules)
      .find((rule) => rule?.order_methods?.find((method) => method === cached_order?.method));

    return rule?.modifier_value < 0 ? rule : null;
  }, [cached_order?.method, category?.smart_pricing_rules, selectedMenu?.smart_pricing_rules]);

  const onChangeVisibility = (visible: boolean) => {
    setVisible(visible);
    visible && onChangeVisibleIndexDebounce();
  };

  const onChangeVisibleIndexDebounce = debounce(() => {
    if (visibleRef.current) {
      onChangeVisibleIndex(index);
    }
  }, 0);

  useIntersection(
    ref,
    {
      rootMargin: '0px',
      threshold: 0,
    },
    false,
    onChangeVisibility,
  );

  const productListSize = useMemo(() => {
    const productsGap = isTwoColumn ? 16 : 0;

    const productsPerColumn = Math.ceil(filteredProducts?.length / (isTwoColumn ? 2 : 1));

    return productsPerColumn * productHeight + (productsPerColumn - 1) * productsGap;
  }, [filteredProducts, isTwoColumn, productHeight]);

  const availableAt = useMemo(() => {
    if (!category?.schedule) {
      return null;
    }

    return WorkScheduleHelpers.formatSchedule(
      category?.schedule ?? selectedMenu?.schedule ?? workSchedule,
      (item: OpeningHoursInterface) => `${formatScheduleUnit(item.open)} - ${formatScheduleUnit(item.close)}`,
      (item: OpeningHoursInterface) =>
        `${formatScheduleUnit(item.open, { day: true, time: true })} - ${formatScheduleUnit(item.close, { day: true, time: true })}`,
    );
  }, [category, selectedMenu, workSchedule, cached_order?.scheduled_for]);

  const isCategoryOpen = useMemo(
    () => (category?.schedule ? WorkScheduleHelpers.isOpen(category.schedule, cached_order?.scheduled_for) : false),
    [category?.schedule, cached_order?.scheduled_for],
  );

  const isCategoryRestricted = Boolean(category?.schedule) && !isCategoryOpen;
  const restrictedTooltip =
    isCategoryRestricted && availableAt ? t('restricted_tooltip', { schedule: availableAt }) : '';

  // "Order again" aggregates products from all over the menu, so it has no schedule of its own to
  // derive a restriction from — each product is restricted by the schedule of ITS category. Without
  // this, a past order would offer time-restricted items as if they were available right now.
  const isAggregatedCategory = category?.type === CategoryType.LastOrder;

  const restrictedTooltipByProductId = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();

    if (!isAggregatedCategory) {
      return map;
    }

    for (const product of category?.products ?? []) {
      const { isRestricted, tooltip } = getRestriction(product.category_ids);

      if (isRestricted) {
        map.set(product.id, tooltip);
      }
    }

    return map;
  }, [isAggregatedCategory, category?.products, getRestriction]);

  const discount = useMemo(() => {
    if (
      !smartPricingRule ||
      category?.type === CategoryType.MostPopular ||
      category?.type === CategoryType.Bogo ||
      category?.type === CategoryType.LastOrder
    ) {
      return null;
    }

    return smartPricingRule.modifier_type === SmartPricingRuleModifierType.Percentage
      ? smartPricingRule.modifier_value + '%'
      : formatCurrency(smartPricingRule.modifier_value);
  }, [smartPricingRule]);

  const renderProducts = useMemo(() => {
    const products = isActive ? filteredProducts : filteredProducts?.slice(0, 2);

    return (
      <div className={classnames({ 'px-md bg-white-100': isMobileApp })}>
        <div
          className={classnames(menuContainer, {
            'relative [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-brand-border':
              isMobileApp || !isTwoColumn,
          })}
          style={{
            gap: isTwoColumn ? theme.spacing.md : 0,
            height: productListSize,
          }}
        >
          {products?.map((product: WithActualPrice<ProductInterface>, index) => (
            <ProductItem
              product={product}
              toggleProduct={() => toggleProduct(product)}
              key={`product-${product?.id}-${index}`}
              discount={discount}
              isRestricted={isCategoryRestricted || restrictedTooltipByProductId.has(product.id)}
              restrictedTooltip={
                isCategoryRestricted ? restrictedTooltip : restrictedTooltipByProductId.get(product.id)
              }
            />
          ))}
        </div>
      </div>
    );
  }, [
    filteredProducts,
    isActive,
    isTwoColumn,
    productListSize,
    discount,
    isMobileApp,
    toggleProduct,
    isCategoryRestricted,
    restrictedTooltip,
    restrictedTooltipByProductId,
  ]);

  const renderDiscountBadge = () => {
    if (!discount) {
      return null;
    }

    return (
      <div className="bg-brand-button-primary-background px-md py-2xs rounded-lg flex items-center justify-center">
        <span className="text-brand-button-primary-text text-xs">{t('discount_badge', { value: discount })}</span>
      </div>
    );
  };

  const renderAvailableAtBadge = () => {
    if (!availableAt) {
      return null;
    }

    const textColor = isCategoryOpen ? SCHEDULE_OPEN_TEXT_CLASS : SCHEDULE_CLOSED_TEXT_CLASS;

    return (
      <div className="bg-brand-card-secondary px-md py-2xs rounded-lg flex items-center justify-center gap-x-[4px]">
        <Clock className={classnames('h-[16px] w-[16px] shrink-0', textColor)} />
        <span className={classnames('text-xs', textColor)}>{t('available_at_badge', { schedule: availableAt })}</span>
      </div>
    );
  };

  if (!filteredProducts.length) {
    return;
  }

  const categoryTitleStyle = classnames('font-semibold text-brand-text-primary', {
    'font-semibold': isMobileApp,
  });

  return (
    <div ref={ref} key={category?.id?.toString()}>
      <div className={classnames('lg:mb-lg mb-md space-y-xs', { 'space-y-[1px] px-md': isMobileApp })}>
        <div className={classnames(categoryTitleContainer)}>
          <div className="flex flex-row items-center justify-start gap-x-[8px] w-full">
            <div>
              <h5 className={categoryTitleStyle}>{category?.name}</h5>
            </div>
            {renderDiscountBadge()}
            {renderAvailableAtBadge()}
          </div>
        </div>
        {category?.description && (
          <p
            className={classnames('text-muted-foreground text-sm', {
              'text-xs': isMobileApp,
            })}
          >
            {category?.description}
          </p>
        )}
      </div>
      {renderProducts}
    </div>
  );
}

const menuContainer = 'grid md:grid-cols-2 grid-cols-1 mt-sm';

const categoryTitleContainer = 'flex flex-row items-center';
